import React, { useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';

const ProductBlock = ({ data, isSelected, onUpdate, previewMode, blockId, onSelectBlock }) => {
  const fileInputRef = useRef(null);
  
  const {
    image = '',
    name = 'Product Name',
    description = 'Product description goes here...',
    price = '$99.99',
    originalPrice = '',
    buttonText = 'Buy Now',
    buttonLink = '#',
    alignment = 'left',
    titleColor = '#333333',
    nameColor = '#333333',
    descriptionColor = '#666666',
    priceColor = '#e74c3c',
    buttonBackgroundColor = '#007bff',
    backgroundColor = 'transparent',
    marginTop = 20,
    marginBottom = 20,
    padding = '20px',
    fontFamily = 'Arial, sans-serif',
  } = data;

  const containerStyles = {
    display: 'flex',
    flexDirection: previewMode === 'mobile' ? 'column' : 'row',
    alignItems: alignment === 'center' ? 'center' : 'flex-start',
    textAlign: alignment,
    padding,
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    backgroundColor: backgroundColor !== 'transparent' ? backgroundColor : undefined,
    border: isSelected ? '2px dashed #007bff' : 'none',
    borderRadius: isSelected ? '4px' : 0,
    gap: '20px',
  };

  const imageStyles = {
    width: previewMode === 'mobile' ? '100%' : '200px',
    height: previewMode === 'mobile' ? '200px' : '150px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    objectFit: 'cover',
    flexShrink: 0,
  };

  const contentStyles = {
    flex: 1,
    minWidth: 0,
  };

  const titleStyles = {
    fontSize: previewMode === 'mobile' ? '20px' : '24px',
    fontWeight: 'bold',
    color: titleColor || nameColor,
    marginBottom: '10px',
    fontFamily,
  };

  const descriptionStyles = {
    fontSize: previewMode === 'mobile' ? '14px' : '16px',
    color: descriptionColor,
    marginBottom: '15px',
    lineHeight: 1.5,
    fontFamily,
  };

  const priceContainerStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '15px',
    justifyContent: alignment === 'center' ? 'center' : alignment === 'right' ? 'flex-end' : 'flex-start',
    flexWrap: 'wrap',
  };

  const priceStyles = {
    fontSize: previewMode === 'mobile' ? '18px' : '20px',
    fontWeight: 'bold',
    color: priceColor,
    fontFamily,
  };

  const originalPriceStyles = {
    fontSize: previewMode === 'mobile' ? '14px' : '16px',
    color: '#999',
    textDecoration: 'line-through',
    fontFamily,
  };

  const buttonStyles = {
    backgroundColor: buttonBackgroundColor,
    color: '#ffffff',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    textTransform: 'none',
    borderRadius: '5px',
    '&:hover': {
      backgroundColor: adjustColor(buttonBackgroundColor, -20),
    },
  };

  function adjustColor(color, amount) {
    if (color.startsWith('#')) {
      const num = parseInt(color.slice(1), 16);
      const r = Math.max(0, Math.min(255, (num >> 16) + amount));
      const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amount));
      const b = Math.max(0, Math.min(255, (num & 0x0000FF) + amount));
      return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    }
    return color;
  }

  const handleButtonClick = (e) => {
    e.preventDefault();
    if (buttonLink && buttonLink !== '#') {
      window.open(buttonLink, '_blank');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const updatedData = { image: e.target.result };
        if (onUpdate) {
          onUpdate(updatedData);
        }
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  const renderImage = () => {
    if (image) {
      return (
        <img
          src={image}
          alt={name}
          style={imageStyles}
          onError={(e) => {
            e.target.style.backgroundColor = '#f8d7da';
            e.target.alt = 'Image failed to load';
          }}
        />
      );
    }
    
    return (
      <Box
        sx={{
          ...imageStyles,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '14px',
          cursor: 'pointer',
          flexDirection: 'column',
          gap: 1,
          '&:hover': {
            backgroundColor: '#e9ecef',
          },
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <CloudUpload sx={{ fontSize: 32 }} />
        <Typography variant="caption">Upload Image</Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={containerStyles}
    >
      {renderImage()}
      
      <Box sx={contentStyles}>
        <Typography sx={titleStyles}>
          {name}
        </Typography>
        
        <Typography sx={descriptionStyles}>
          {description}
        </Typography>
        
        <Box sx={priceContainerStyles}>
          <Typography sx={priceStyles}>
            {price}
          </Typography>
          {originalPrice && (
            <Typography sx={originalPriceStyles}>
              {originalPrice}
            </Typography>
          )}
        </Box>
        
        {buttonText && (
          <Button
            sx={buttonStyles}
            onClick={handleButtonClick}
          >
            {buttonText}
          </Button>
        )}
      </Box>
      
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*"
        style={{ display: 'none' }}
      />
    </Box>
  );
};

export default ProductBlock; 